import { z } from 'zod';
import {
  MANUAL_PAYMENT_METHODS,
  ORDER_STATUSES,
  PAYMENT_METHODS,
  PAYMENT_PURPOSES,
  PAYMENT_RECORD_PROVIDERS,
  PAYMENT_STATUSES,
  PAYMENT_TRANSACTION_TYPES,
  REFUND_STATUSES,
} from '@bulk/types';
import { dateTimeSchema, idSchema, pageSchema, paginationQuerySchema, sortOrderSchema } from './common';

export const createCheckoutSchema = z.object({
  planId: idSchema,
  memberId: idSchema.optional(),
  membershipId: idSchema.optional(),
  returnUrl: z.url().optional(),
  locale: z.enum(['en', 'ka']).optional(),
  saveCard: z.boolean().optional(),
  promoCode: z.string().trim().min(1).max(32).optional().describe('A promo code; the lowest of the sale, the new-member offer and the code wins. 422 PROMO_CODE_INVALID with a reason when it gives nothing'),
});
export type CreateCheckoutInput = z.infer<typeof createCheckoutSchema>;

export const savedCardSchema = z.object({
  id: idSchema,
  provider: z.enum(PAYMENT_RECORD_PROVIDERS),
  cardMask: z.string().nullable(),
  expiry: z.string().nullable(),
  createdAt: z.string(),
});
export type SavedCardDto = z.infer<typeof savedCardSchema>;

export const autoRenewSchema = z.object({ autoRenew: z.boolean() });

export const checkoutResultSchema = z.object({
  orderId: idSchema,
  paymentId: idSchema,
  provider: z.enum(PAYMENT_RECORD_PROVIDERS),
  redirectUrl: z.string().nullable(),
  status: z.enum(PAYMENT_STATUSES),
});
export type CheckoutResultDto = z.infer<typeof checkoutResultSchema>;

export const orderSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  memberId: idSchema,
  planId: idSchema,
  membershipId: idSchema.nullable(),
  amountMinor: z.number().int(),
  listPriceMinor: z.number().int(),
  campaignId: idSchema.nullable(),
  campaignPct: z.number().int().nullable(),
  currency: z.string(),
  status: z.enum(ORDER_STATUSES),
  description: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type OrderDto = z.infer<typeof orderSchema>;

export const paymentSchema = z.object({
  id: idSchema,
  gymId: idSchema,
  purpose: z.enum(PAYMENT_PURPOSES).describe('WALLET_TOP_UP is money put on the member\'s prepaid balance: in the ledger, never in revenue'),
  orderId: idSchema.nullable().describe('The membership order; null for a balance top-up'),
  memberId: idSchema,
  memberName: z.string().nullable(),
  provider: z.enum(PAYMENT_RECORD_PROVIDERS),
  method: z.enum(PAYMENT_METHODS),
  providerPaymentId: z.string().nullable(),
  receiptNumber: z.string().nullable(),
  invoiceNumber: z.string().nullable(),
  recordedByUserId: idSchema.nullable(),
  note: z.string().nullable(),
  amountMinor: z.number().int(),
  refundedMinor: z.number().int(),
  currency: z.string(),
  status: z.enum(PAYMENT_STATUSES),
  description: z.string().nullable(),
  paidAt: z.string().nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PaymentDto = z.infer<typeof paymentSchema>;

export const paymentPageSchema = pageSchema(paymentSchema).extend({
  /** The branches listed, for staff who work only at some and list without a member; null for the whole gym. */
  branches: z.array(z.string()).nullable(),
});
export type PaymentPageDto = z.infer<typeof paymentPageSchema>;

export const paymentTransactionSchema = z.object({
  id: idSchema,
  paymentId: idSchema,
  type: z.enum(PAYMENT_TRANSACTION_TYPES),
  providerTransactionId: z.string().nullable(),
  amountMinor: z.number().int(),
  status: z.string(),
  createdAt: z.string(),
});
export type PaymentTransactionDto = z.infer<typeof paymentTransactionSchema>;

export const refundRequestSchema = z.object({
  amountMinor: z.number().int().positive().optional(),
  reason: z.string().trim().max(500).optional(),
});
export type RefundRequestInput = z.infer<typeof refundRequestSchema>;

export const refundSchema = z.object({
  id: idSchema,
  paymentId: idSchema,
  amountMinor: z.number().int(),
  status: z.enum(REFUND_STATUSES),
  reason: z.string().nullable(),
  providerRefundId: z.string().nullable(),
  requestedByUserId: idSchema.nullable(),
  reviewedByUserId: idSchema.nullable(),
  reviewedAt: z.string().nullable(),
  reviewNote: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type RefundDto = z.infer<typeof refundSchema>;

export const reviewRefundSchema = z.object({
  decision: z.enum(['APPROVE', 'REJECT']),
  note: z.string().trim().max(500).optional(),
});
export type ReviewRefundInput = z.infer<typeof reviewRefundSchema>;

export const receiptSchema = z.object({
  issuedAt: z.string(),
  gym: z.object({
    name: z.string(),
    legalName: z.string().nullable(),
    address: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    timezone: z.string(),
  }),
  payment: paymentSchema,
  member: z.object({ id: idSchema, name: z.string(), memberNumber: z.string(), email: z.string().nullable() }),
  item: z.object({ description: z.string(), planName: z.string().nullable() }),
  membership: z.object({ id: idSchema, status: z.string(), startsAt: z.string().nullable(), endsAt: z.string().nullable() }).nullable(),
  recordedByName: z.string().nullable(),
  refunds: z.array(refundSchema),
});
export type ReceiptDto = z.infer<typeof receiptSchema>;

export const PAYMENT_LIST_SORTS = ['createdAt', 'date', 'member', 'amount', 'status'] as const;

export const paymentListQuerySchema = paginationQuerySchema.extend({
  status: z.enum(PAYMENT_STATUSES).optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  memberId: idSchema.optional(),
  from: dateTimeSchema.optional(),
  to: dateTimeSchema.optional(),
  sort: z.enum(PAYMENT_LIST_SORTS).default('createdAt').describe('`date` is when it was paid, or created while unpaid; `member` is last name, then first name'),
  order: sortOrderSchema.default('desc'),
});
export type PaymentListQuery = z.infer<typeof paymentListQuerySchema>;

export const recordManualPaymentSchema = z.object({
  memberId: idSchema,
  planId: idSchema,
  method: z.enum(MANUAL_PAYMENT_METHODS),
  startsAt: dateTimeSchema.optional(),
  note: z.string().trim().max(500).optional(),
  receiptNumber: z.string().trim().min(1).max(40).optional(),
  soldByUserId: idSchema.optional().describe('The staff member credited with the sale, from `GET /targets/sellers`; the caller when left out'),
  promoCode: z.string().trim().min(1).max(32).optional().describe('A promo code the member brought to the desk; 422 PROMO_CODE_INVALID with a reason when it gives nothing'),
});
export type RecordManualPaymentInput = z.infer<typeof recordManualPaymentSchema>;

export const mockPaymentDecisionSchema = z.object({
  outcome: z.enum(['PAID', 'FAILED', 'CANCELLED']),
});
export type MockPaymentDecision = z.infer<typeof mockPaymentDecisionSchema>;
